/**
 * LetterheadStudio — a Figma-style, single-canvas letterhead designer that
 * replaces the previous 3-tab (Letterhead / Layout / Field-styles) UI with
 * one unified paradigm:
 *
 *   ┌──────────┬─────────────────────────────┬──────────┐
 *   │  Layers  │        Live A4 canvas       │ Inspector│
 *   │  panel   │  (click any zone to edit)   │          │
 *   └──────────┴─────────────────────────────┴──────────┘
 *
 * Everything the lab admin used to configure in 3 tabs (slot map, colours,
 * borders, heights, per-slot alignment, per-image sizes, secondary rows,
 * backgrounds, field styles) is now reachable from either the Layers panel
 * (accordion) or by clicking the corresponding zone in the live preview.
 *
 * The A4 preview mirrors the actual printed page 1:1 (px = mm × 3.78 at
 * ~96dpi) so what the lab admin sees IS what patients get.
 *
 * Backend stays Dict[str, Any] — every new field just flows through the
 * `letterhead` blob on the lab document.
 */
import React, { useMemo, useState, useEffect, useRef } from "react";
import { ChevronDown, ChevronRight, Layers as LayersIcon, Palette, Frame as FrameIcon, Type as TypeIcon, Move, Sparkles, Eye, Undo2, Redo2, Save, MonitorSmartphone, Award, PenLine, Plus, Trash2, Star, FileImage, Upload, ExternalLink, Wand2, GripVertical, LayoutTemplate } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import LetterheadLivePreview from "@/pages/lab/LetterheadLivePreview";
import { LETTERHEAD_FONT_LABELS } from "@/pages/report/SmartReport";
import { api, apiError } from "@/lib/api";
import { useSiteSettings } from "@/lib/settings";
import { LETTERHEAD_PRESETS, applyPreset } from "@/pages/lab/letterheadPresets";

/* ============================================================
   4-zone border config — supersedes the flat legacy border fields.
   Each zone has independent top/bottom thickness, style, colour.
   ============================================================ */
const BORDER_ZONES = [
  { key: "primary_header",   label: "Primary header",   accent: "text-efgh-blue" },
  { key: "secondary_header", label: "Secondary header", accent: "text-efgh-yellow" },
  { key: "secondary_footer", label: "Secondary footer", accent: "text-efgh-orange" },
  { key: "primary_footer",   label: "Primary footer",   accent: "text-efgh-green" },
];

const SLOT_CHOICES_HEADER = [
  { k: "none", n: "— Empty" },
  { k: "logo", n: "Lab logo" }, { k: "name", n: "Lab name" }, { k: "name_address", n: "Name + address" },
  { k: "contact", n: "Contact stack" }, { k: "phone", n: "Phone" }, { k: "email", n: "Email" }, { k: "website", n: "Website" },
  { k: "whatsapp", n: "WhatsApp" }, { k: "address", n: "Address" }, { k: "city", n: "City" },
  { k: "accreditation", n: "Accreditation logos" }, { k: "custom_logos", n: "Custom logos" },
  { k: "custom_header_text", n: "Custom header text" }, { k: "tagline", n: "Tagline" },
  { k: "license", n: "License no." }, { k: "gst", n: "GST no." }, { k: "cin", n: "CIN no." },
  { k: "hours", n: "Working hours" }, { k: "customer_care", n: "Customer care no." }, { k: "toll_free", n: "Toll-free no." },
  { k: "emergency", n: "Emergency contact" }, { k: "socials", n: "Social handles" },
];
const SLOT_CHOICES_FOOTER = [
  ...SLOT_CHOICES_HEADER,
  { k: "signatures", n: "Doctor signatures" }, { k: "signature", n: "Signature line" },
  { k: "custom_footer_text", n: "Custom footer text" },
  { k: "generated_at", n: "Generated timestamp" }, { k: "page_number", n: "Page X of Y" },
];

const BORDER_STYLES = ["solid", "dashed", "dotted", "double"];

export function LetterheadStudio({ lab, form, setForm, save, addAccreditation, removeAccreditation, addCustomLogo, removeCustomLogo, addSignature, removeSignature, setDefaultSignature, patchAssetItem, templateMode = false }) {
  const lh = useMemo(() => form.letterhead || {}, [form.letterhead]);
  const [selectedZone, setSelectedZone] = useState("primary_header");
  const [expandedGroups, setExpandedGroups] = useState({ layers: true, colours: false, borders: false, typography: false, spacing: false, assets: false, covers: false });
  const [history, setHistory] = useState([]);
  const [historyIdx, setHistoryIdx] = useState(-1);
  const [saving, setSaving] = useState(false);
  const [presetsOpen, setPresetsOpen] = useState(false);

  const set = (k, v) => {
    const next = { ...lh, [k]: v };
    // Push previous state to history for undo
    setHistory((h) => [...h.slice(0, historyIdx + 1), lh]);
    setHistoryIdx((i) => i + 1);
    setForm({ ...form, letterhead: next });
  };
  const undo = () => {
    if (historyIdx < 0) return;
    setForm({ ...form, letterhead: history[historyIdx] });
    setHistoryIdx((i) => i - 1);
  };
  const redo = () => {
    if (historyIdx + 1 >= history.length) return;
    setHistoryIdx((i) => i + 1);
    setForm({ ...form, letterhead: history[historyIdx + 1] });
  };

  const doSave = async () => {
    setSaving(true);
    try {
      await save();
      toast.success("Letterhead saved");
    } catch (e) { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const toggleGroup = (g) => setExpandedGroups((s) => ({ ...s, [g]: !s[g] }));

  return (
    <div className="rounded-2xl border border-border bg-white overflow-hidden" data-testid="letterhead-studio">
      {/* ============ Top bar ============ */}
      <div className="border-b border-border bg-muted/30 px-3 py-2 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Sparkles className="w-4 h-4 text-efgh-orange" />
          <div>
            <div className="font-heading font-semibold text-sm text-ink">Letterhead studio</div>
            <div className="text-[10px] text-ink/50 hidden sm:block">Design once — every Smart Report uses this letterhead</div>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {!templateMode && (
            <>
              <button onClick={() => setPresetsOpen(true)} data-testid="studio-open-presets" title="Preset templates" className="inline-flex items-center gap-1.5 px-2.5 h-8 rounded-md border border-efgh-orange/40 text-efgh-orange hover:bg-efgh-orange/10 text-xs font-medium">
                <LayoutTemplate className="w-3.5 h-3.5" /> Templates
              </button>
              <div className="w-px h-6 bg-border mx-1" />
            </>
          )}
          <button onClick={undo} disabled={historyIdx < 0} data-testid="studio-undo" title="Undo" className="p-2 rounded-md hover:bg-white border border-border disabled:opacity-40 disabled:cursor-not-allowed">
            <Undo2 className="w-3.5 h-3.5" />
          </button>
          <button onClick={redo} disabled={historyIdx + 1 >= history.length} data-testid="studio-redo" title="Redo" className="p-2 rounded-md hover:bg-white border border-border disabled:opacity-40 disabled:cursor-not-allowed">
            <Redo2 className="w-3.5 h-3.5" />
          </button>
          <div className="w-px h-6 bg-border mx-1" />
          <Button onClick={doSave} disabled={saving} data-testid="studio-save" size="sm" className="bg-efgh-green hover:bg-efgh-green/90 text-white h-8">
            <Save className="w-3.5 h-3.5 mr-1.5" /> {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>

      {presetsOpen && (
        <PresetsModal
          onClose={() => setPresetsOpen(false)}
          onApply={(p) => {
            setForm((prev) => ({ ...prev, letterhead: applyPreset(prev.letterhead || {}, p.preset) }));
            toast.success(`${p.name} template applied — remember to save`);
            setPresetsOpen(false);
          }}
        />
      )}

      {/* ============ Body — three columns (narrower rails so preview gets ~30% more width) ============ */}
      <div className="grid lg:grid-cols-[220px_1fr_240px] grid-cols-1 min-h-[600px]">
        {/* -------- LEFT: Layers panel -------- */}
        <div className="border-r border-border bg-muted/10 p-3 overflow-y-auto max-h-[calc(100vh-260px)]">
          <Group icon={LayersIcon} label="Layers" expanded={expandedGroups.layers} onToggle={() => toggleGroup("layers")}>
            {BORDER_ZONES.map((z) => (
              <button key={z.key} onClick={() => setSelectedZone(z.key)} data-testid={`layer-${z.key}`}
                className={`w-full text-left px-2.5 py-2 rounded-lg text-xs flex items-center justify-between gap-2 transition-colors ${selectedZone === z.key ? "bg-efgh-blue/10 text-efgh-blue font-medium border border-efgh-blue/30" : "hover:bg-white text-ink/70 border border-transparent"}`}>
                <span className="flex items-center gap-2 min-w-0">
                  <span className={`w-1.5 h-1.5 rounded-full ${z.accent === "text-efgh-blue" ? "bg-efgh-blue" : z.accent === "text-efgh-yellow" ? "bg-efgh-yellow" : z.accent === "text-efgh-orange" ? "bg-efgh-orange" : "bg-efgh-green"}`} />
                  <span className="truncate">{z.label}</span>
                </span>
                {(z.key === "secondary_header" || z.key === "secondary_footer") && (
                  <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${lh[`${z.key === "secondary_header" ? "secondary_header" : "secondary_footer"}_enabled`] ? "bg-efgh-green/15 text-efgh-green" : "bg-muted text-ink/40"}`}>
                    {lh[`${z.key === "secondary_header" ? "secondary_header" : "secondary_footer"}_enabled`] ? "ON" : "OFF"}
                  </span>
                )}
              </button>
            ))}
          </Group>

          <Group icon={Palette} label="Global colours" expanded={expandedGroups.colours} onToggle={() => toggleGroup("colours")}>
            <ColorRow label="Primary" value={lh.primary_color || "#0B7A4D"} onChange={(v) => set("primary_color", v)} testid="studio-color-primary" />
            <ColorRow label="Accent"  value={lh.accent_color  || "#F4B400"} onChange={(v) => set("accent_color", v)}  testid="studio-color-accent" />
            <ColorRow label="Text"    value={lh.text_color    || "#374151"} onChange={(v) => set("text_color", v)}    testid="studio-color-text" />
          </Group>

          <Group icon={TypeIcon} label="Typography" expanded={expandedGroups.typography} onToggle={() => toggleGroup("typography")}>
            <div className="px-1 py-1">
              <Label className="text-[10px] uppercase tracking-wider text-ink/50">Font family</Label>
              <select value={lh.font_family || "system"} onChange={(e) => set("font_family", e.target.value)} data-testid="studio-font-family"
                className="mt-1 h-8 w-full rounded-md border border-border bg-white px-2 text-xs">
                {Object.entries(LETTERHEAD_FONT_LABELS).map(([k, label]) => (
                  <option key={k} value={k}>{label}</option>
                ))}
              </select>
            </div>
            <div className="px-1 py-1">
              <Label className="text-[10px] uppercase tracking-wider text-ink/50">Base size (px)</Label>
              <Input type="number" min={8} max={20} value={lh.base_text_size ?? 11} onChange={(e) => set("base_text_size", parseInt(e.target.value, 10) || 11)} data-testid="studio-base-size" className="h-8 text-xs" />
            </div>
          </Group>

          <Group icon={Move} label="Spacing" expanded={expandedGroups.spacing} onToggle={() => toggleGroup("spacing")}>
            <SliderRow label="Header height (mm)" value={lh.header_height_mm ?? 24} min={12} max={70} onChange={(v) => set("header_height_mm", v)} testid="studio-header-height" />
            <SliderRow label="Footer height (mm)" value={lh.footer_height_mm ?? 16} min={8} max={50} onChange={(v) => set("footer_height_mm", v)} testid="studio-footer-height" />
            <SliderRow label="Left margin (mm)"   value={lh.margin_left_mm ?? 12} min={4} max={30} onChange={(v) => set("margin_left_mm", v)} testid="studio-ml" />
            <SliderRow label="Right margin (mm)"  value={lh.margin_right_mm ?? 12} min={4} max={30} onChange={(v) => set("margin_right_mm", v)} testid="studio-mr" />
          </Group>

          <Group icon={Award} label="Assets" expanded={expandedGroups.assets} onToggle={() => toggleGroup("assets")}>
            {templateMode ? (
              <div className="text-[10px] text-ink/40 italic p-2">
                Assets (logos, signatures) are per-lab and can&apos;t be attached to templates. Labs upload their own after applying the template.
              </div>
            ) : (
              <AssetsGroup
                form={form}
                addAccreditation={addAccreditation}
                removeAccreditation={removeAccreditation}
                addCustomLogo={addCustomLogo}
                removeCustomLogo={removeCustomLogo}
                addSignature={addSignature}
                removeSignature={removeSignature}
                setDefaultSignature={setDefaultSignature}
                patchAssetItem={patchAssetItem}
              />
            )}
          </Group>

          {!templateMode && (
            <Group icon={FileImage} label="PDF covers" expanded={expandedGroups.covers} onToggle={() => toggleGroup("covers")}>
              <CoversGroup form={form} setForm={setForm} lab={lab} />
            </Group>
          )}
        </div>

        {/* -------- CENTER: Live canvas (real dummy Smart Report + view mode toggles) -------- */}
        <div className="bg-slate-100 p-4 overflow-auto max-h-[calc(100vh-260px)]">
          <div className="mx-auto" style={{ maxWidth: 820 }}>
            {/* Zone markers for the primary header/footer (click to focus that zone in inspector) */}
            <button
              type="button"
              onClick={() => setSelectedZone("primary_header")}
              data-testid="canvas-focus-primary-header"
              className={`w-full mb-2 border-2 border-dashed rounded-md p-1.5 text-[9px] uppercase tracking-widest text-center transition-colors ${selectedZone === "primary_header" ? "border-efgh-blue bg-efgh-blue/10 text-efgh-blue" : "border-efgh-blue/30 bg-efgh-blue/[0.03] text-efgh-blue/70 hover:bg-efgh-blue/[0.08]"}`}
            >
              ▲ Click to edit primary header ▲
            </button>
            <LetterheadLivePreview form={form} compact defaultMode="pdf" />
            <button
              type="button"
              onClick={() => setSelectedZone("primary_footer")}
              data-testid="canvas-focus-primary-footer"
              className={`w-full mt-2 border-2 border-dashed rounded-md p-1.5 text-[9px] uppercase tracking-widest text-center transition-colors ${selectedZone === "primary_footer" ? "border-efgh-green bg-efgh-green/10 text-efgh-green" : "border-efgh-green/30 bg-efgh-green/[0.03] text-efgh-green/70 hover:bg-efgh-green/[0.08]"}`}
            >
              ▼ Click to edit primary footer ▼
            </button>
          </div>
        </div>

        {/* -------- RIGHT: Inspector -------- */}
        <div className="border-l border-border bg-muted/10 p-3 overflow-y-auto max-h-[calc(100vh-260px)]">
          <ZoneInspector zone={selectedZone} lh={lh} set={set} />
        </div>
      </div>

      {/* Mobile fallback hint */}
      <div className="lg:hidden border-t border-border bg-amber-50/60 px-3 py-2 text-[11px] text-amber-800 flex items-center gap-2">
        <MonitorSmartphone className="w-3.5 h-3.5" /> Studio best on desktop · mobile shows a stacked simplified view.
      </div>
    </div>
  );
}

/* ============================================================
   ZoneInspector — right rail contextual editor per selected zone
   ============================================================ */
function ZoneInspector({ zone, lh, set }) {
  const isSecondary = zone.startsWith("secondary_");
  const isFooter = zone.endsWith("_footer");
  const prefix = zone; // primary_header | secondary_header | primary_footer | secondary_footer
  const slotBase = { primary_header: "header", secondary_header: "sec_header", primary_footer: "footer", secondary_footer: "sec_footer" }[prefix];
  const label = { primary_header: "Primary header", secondary_header: "Secondary header", primary_footer: "Primary footer", secondary_footer: "Secondary footer" }[prefix];
  const CHOICES = isFooter ? SLOT_CHOICES_FOOTER : SLOT_CHOICES_HEADER;
  const enabledKey = isSecondary ? `${prefix}_enabled` : null;

  // Collect every field-key active in any of the 3 slots of the current zone so we can
  // render per-field text & icon controls only for what the admin has actually placed.
  // Row-break sentinels are excluded because they're layout markers, not real fields.
  const activeFields = useMemo(() => {
    const out = new Set();
    ["left", "center", "right"].forEach((side) => {
      const v = lh[`${slotBase}_${side}`];
      const arr = Array.isArray(v) ? v : (v && v !== "none" ? [v] : []);
      arr.forEach((k) => { if (k && k !== "__break__") out.add(k); });
    });
    return Array.from(out);
  }, [lh, slotBase]);

  return (
    <div className="space-y-3" data-testid={`inspector-${zone}`}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-wider text-ink/50">Editing zone</div>
      </div>
      <h3 className="font-heading font-semibold text-ink text-sm">{label}</h3>
      {isSecondary && (
        <label className="flex items-center gap-2 text-xs">
          <input type="checkbox" checked={!!lh[enabledKey]} onChange={(e) => set(enabledKey, e.target.checked)} data-testid={`inspector-${zone}-enable`} className="accent-efgh-blue" />
          Enable {label.toLowerCase()}
        </label>
      )}

      <SectionCard title="Slots">
        <SlotRow label="Left"   value={lh[`${slotBase}_left`]}   choices={CHOICES} onChange={(v) => set(`${slotBase}_left`, v)}   testid={`inspector-${zone}-left`} />
        <SlotRow label="Center" value={lh[`${slotBase}_center`]} choices={CHOICES} onChange={(v) => set(`${slotBase}_center`, v)} testid={`inspector-${zone}-center`} />
        <SlotRow label="Right"  value={lh[`${slotBase}_right`]}  choices={CHOICES} onChange={(v) => set(`${slotBase}_right`, v)}  testid={`inspector-${zone}-right`} />
      </SectionCard>

      <SectionCard title="Slot layout & spacing">
        {["left", "center", "right"].map((side) => {
          const key = `${slotBase}_${side}`;
          const dir = (lh.slot_layout || {})[key] || "column";
          const gapX = (lh.slot_gap_x || {})[key] ?? 10;
          const gapY = (lh.slot_gap_y || {})[key] ?? 4;
          return (
            <div key={key} className="rounded-md border border-border p-1.5 space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-wider text-ink/60 capitalize">{side}</span>
                <div className="flex rounded border border-border overflow-hidden">
                  <button type="button" onClick={() => set("slot_layout", { ...(lh.slot_layout || {}), [key]: "column" })} data-testid={`inspector-${zone}-${side}-layout-column`} className={`px-1.5 py-0.5 text-[10px] ${dir === "column" ? "bg-ink text-white" : "bg-white text-ink/60"}`}>Stack</button>
                  <button type="button" onClick={() => set("slot_layout", { ...(lh.slot_layout || {}), [key]: "row" })} data-testid={`inspector-${zone}-${side}-layout-row`} className={`px-1.5 py-0.5 text-[10px] ${dir === "row" ? "bg-ink text-white" : "bg-white text-ink/60"}`}>Row</button>
                </div>
              </div>
              <SliderRow label="Horiz gap (px)" value={gapX} min={0} max={40} onChange={(v) => set("slot_gap_x", { ...(lh.slot_gap_x || {}), [key]: v })} testid={`inspector-${zone}-${side}-gap-x`} />
              <SliderRow label="Vert gap (px)"  value={gapY} min={0} max={40} onChange={(v) => set("slot_gap_y", { ...(lh.slot_gap_y || {}), [key]: v })} testid={`inspector-${zone}-${side}-gap-y`} />
            </div>
          );
        })}
      </SectionCard>

      <SectionCard title="Background">
        <ColorRow label="BG colour" value={lh[`${prefix === "primary_header" ? "header" : prefix === "primary_footer" ? "footer" : prefix.replace("secondary_", "sec_")}_bg_color`] || "#ffffff"} onChange={(v) => set(`${prefix === "primary_header" ? "header" : prefix === "primary_footer" ? "footer" : prefix.replace("secondary_", "sec_")}_bg_color`, v)} testid={`inspector-${zone}-bg`} />
      </SectionCard>

      <SectionCard title="Border">
        <SliderRow label="Top thickness (px)"    value={lh[`${prefix}_border_top_thickness`] ?? (prefix === "primary_header" ? (lh.border_above_header_thickness ?? 0) : 0)} min={0} max={8} onChange={(v) => set(`${prefix}_border_top_thickness`, v)} testid={`inspector-${zone}-border-top`} />
        <SliderRow label="Bottom thickness (px)" value={lh[`${prefix}_border_bottom_thickness`] ?? (prefix === "primary_header" ? (lh.border_below_header_thickness ?? 1) : 0)} min={0} max={8} onChange={(v) => set(`${prefix}_border_bottom_thickness`, v)} testid={`inspector-${zone}-border-bottom`} />
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-ink/50">Style</Label>
            <select value={lh[`${prefix}_border_style`] || lh.border_style || "solid"} onChange={(e) => set(`${prefix}_border_style`, e.target.value)} data-testid={`inspector-${zone}-border-style`}
              className="h-8 w-full rounded-md border border-border bg-white px-2 text-xs">
              {BORDER_STYLES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <ColorRow label="Colour" value={lh[`${prefix}_border_color`] || lh.border_color || "#0B7A4D"} onChange={(v) => set(`${prefix}_border_color`, v)} testid={`inspector-${zone}-border-color`} />
        </div>
      </SectionCard>

      <SectionCard title="Alignment within slots">
        {["left", "center", "right"].map((side) => {
          const k = `${slotBase}_${side}`;
          const align = (lh.slot_align || {})[k] || "start";
          return (
            <div key={k} className="flex items-center gap-2">
              <span className="text-[10px] text-ink/50 capitalize w-12 shrink-0">{side}</span>
              <select value={align} onChange={(e) => set("slot_align", { ...(lh.slot_align || {}), [k]: e.target.value })} data-testid={`inspector-${zone}-align-${side}`}
                className="h-7 rounded-md border border-border bg-white px-1 text-[11px] flex-1">
                <option value="start">Left</option>
                <option value="center">Center</option>
                <option value="end">Right</option>
              </select>
            </div>
          );
        })}
      </SectionCard>

      {activeFields.length > 0 && (
        <SectionCard title="Text & icons (per field)">
          {activeFields.map((fk) => (
            <FieldStyleEditor key={fk} fieldKey={fk} lh={lh} set={set} choices={CHOICES} />
          ))}
        </SectionCard>
      )}
    </div>
  );
}

/* ============================================================
   FieldStyleEditor — per-field text + icon override (inside Inspector)
   Writes to lh.field_styles[fieldKey] = { family, size, weight, color, icon_color, icon_size, icon_hidden }
   ============================================================ */
function FieldStyleEditor({ fieldKey, lh, set, choices }) {
  const [open, setOpen] = useState(false);
  const fs = (lh.field_styles || {})[fieldKey] || {};
  const label = (choices.find((c) => c.k === fieldKey)?.n) || fieldKey;
  const setFs = (patch) => {
    const next = { ...(lh.field_styles || {}) };
    const merged = { ...(next[fieldKey] || {}), ...patch };
    // Prune empties so the blob stays lean.
    Object.keys(merged).forEach((k) => { if (merged[k] === undefined || merged[k] === "" || merged[k] === null) delete merged[k]; });
    if (Object.keys(merged).length === 0) delete next[fieldKey]; else next[fieldKey] = merged;
    set("field_styles", next);
  };
  const clear = () => {
    const next = { ...(lh.field_styles || {}) };
    delete next[fieldKey];
    set("field_styles", next);
  };
  const hasOverride = Object.keys(fs).length > 0;
  return (
    <div className={`rounded-md border ${hasOverride ? "border-efgh-green/40 bg-efgh-green/[0.04]" : "border-border"} p-1.5`} data-testid={`fs-editor-${fieldKey}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between text-[11px] font-medium text-ink" data-testid={`fs-editor-${fieldKey}-toggle`}>
        <span className="flex items-center gap-1.5">
          {open ? <ChevronDown className="w-3 h-3 text-ink/40" /> : <ChevronRight className="w-3 h-3 text-ink/40" />}
          {label}
          {hasOverride && <span className="text-[8px] px-1 py-0.5 rounded bg-efgh-green/15 text-efgh-green">custom</span>}
        </span>
        {hasOverride && <button type="button" onClick={(e) => { e.stopPropagation(); clear(); }} data-testid={`fs-editor-${fieldKey}-reset`} className="text-[9px] text-ink/40 hover:text-danger">reset</button>}
      </button>
      {open && (
        <div className="mt-1.5 pt-1.5 border-t border-border/60 space-y-1.5">
          <div>
            <Label className="text-[9px] uppercase tracking-wider text-ink/50">Font</Label>
            <select value={fs.family || ""} onChange={(e) => setFs({ family: e.target.value })} data-testid={`fs-editor-${fieldKey}-family`} className="h-7 w-full rounded border border-border bg-white px-1 text-[11px]">
              <option value="">— inherit —</option>
              {Object.entries(LETTERHEAD_FONT_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            <div>
              <Label className="text-[9px] uppercase tracking-wider text-ink/50">Size (px)</Label>
              <Input type="number" min={7} max={40} value={fs.size ?? ""} onChange={(e) => setFs({ size: e.target.value ? parseInt(e.target.value, 10) : undefined })} data-testid={`fs-editor-${fieldKey}-size`} placeholder="—" className="h-7 text-[11px]" />
            </div>
            <div>
              <Label className="text-[9px] uppercase tracking-wider text-ink/50">Weight</Label>
              <select value={fs.weight ?? ""} onChange={(e) => setFs({ weight: e.target.value ? parseInt(e.target.value, 10) : undefined })} data-testid={`fs-editor-${fieldKey}-weight`} className="h-7 w-full rounded border border-border bg-white px-1 text-[11px]">
                <option value="">— inherit —</option>
                <option value={300}>300 · Light</option>
                <option value={400}>400 · Regular</option>
                <option value={500}>500 · Medium</option>
                <option value={600}>600 · Semibold</option>
                <option value={700}>700 · Bold</option>
                <option value={800}>800 · Extra</option>
              </select>
            </div>
          </div>
          <ColorRow label="Text colour" value={fs.color || lh.text_color || "#374151"} onChange={(v) => setFs({ color: v })} testid={`fs-editor-${fieldKey}-color`} />
          <div className="pt-1 mt-1 border-t border-border/60 space-y-1">
            <div className="text-[9px] uppercase tracking-wider text-ink/40">Icon</div>
            <ColorRow label="Icon colour" value={fs.icon_color || lh.icon_color || "#0B7A4D"} onChange={(v) => setFs({ icon_color: v })} testid={`fs-editor-${fieldKey}-icon-color`} />
            <SliderRow label="Icon size (px)" value={fs.icon_size ?? (lh.icon_size ?? 12)} min={8} max={28} onChange={(v) => setFs({ icon_size: v })} testid={`fs-editor-${fieldKey}-icon-size`} />
            <label className="flex items-center gap-1.5 text-[10px]">
              <input type="checkbox" checked={!!fs.icon_hidden} onChange={(e) => setFs({ icon_hidden: e.target.checked ? true : undefined })} data-testid={`fs-editor-${fieldKey}-icon-hidden`} className="accent-efgh-blue" />
              Hide icon
            </label>
          </div>
        </div>
      )}
    </div>
  );
}

/* ============================================================
   AssetsGroup — accreditation / custom logos / signatures inline inside the Studio
   ============================================================ */
function AssetsGroup({ form, addAccreditation, removeAccreditation, addCustomLogo, removeCustomLogo, addSignature, removeSignature, setDefaultSignature, patchAssetItem }) {
  return (
    <div className="space-y-3" data-testid="studio-assets-group">
      <AssetSubGroup
        title="Accreditation"
        icon={Award}
        color="text-efgh-orange"
        items={form.accreditation_logos || []}
        onAdd={addAccreditation}
        onRemove={removeAccreditation}
        onPatch={(i, patch) => patchAssetItem && patchAssetItem("accreditation", i, patch)}
        renderItem={(a, i) => (
          <>
            <img src={a.logo_url} alt={a.label} className="w-6 h-6 rounded object-contain bg-muted" />
            <span className="text-[10px] flex-1 truncate">{a.label}</span>
          </>
        )}
        addLabel="Add accreditation"
        testidBase="studio-accred"
        sizeDefault={36}
        sizeMax={100}
      />
      <AssetSubGroup
        title="Custom logos"
        icon={Award}
        color="text-efgh-green"
        items={form.custom_logos || []}
        onAdd={addCustomLogo}
        onRemove={removeCustomLogo}
        onPatch={(i, patch) => patchAssetItem && patchAssetItem("custom", i, patch)}
        renderItem={(c, i) => (
          <>
            <img src={c.logo_url} alt={c.label} className="w-6 h-6 rounded object-contain bg-muted" />
            <span className="text-[10px] flex-1 truncate">{c.label}</span>
          </>
        )}
        addLabel="Add custom logo"
        testidBase="studio-custom"
        sizeDefault={36}
        sizeMax={120}
      />
      <AssetSubGroup
        title="Doctor signatures"
        icon={PenLine}
        color="text-efgh-orange"
        items={form.doctor_signatures || []}
        onAdd={addSignature}
        onRemove={removeSignature}
        onPatch={(i, patch) => patchAssetItem && patchAssetItem("signature", i, patch)}
        renderItem={(s, i) => (
          <>
            <img src={s.signature_url} alt={s.name} className="w-10 h-6 object-contain bg-muted rounded" />
            <span className="text-[10px] flex-1 truncate">Dr. {s.name}</span>
            <button type="button" onClick={() => setDefaultSignature && setDefaultSignature(i)} data-testid={`studio-sig-default-${i}`} title="Set default" className={`p-0.5 ${s.is_default ? "text-efgh-yellow" : "text-ink/30 hover:text-ink/60"}`}>
              <Star className="w-3 h-3" fill={s.is_default ? "currentColor" : "none"} />
            </button>
          </>
        )}
        addLabel="Add signature"
        testidBase="studio-sig"
        sizeDefault={56}
        sizeMax={140}
      />
    </div>
  );
}

function AssetSubGroup({ title, icon: Icon, color, items, onAdd, onRemove, onPatch, renderItem, addLabel, testidBase, sizeDefault, sizeMax }) {
  return (
    <div className="rounded-md border border-border bg-white p-1.5">
      <div className="flex items-center justify-between mb-1">
        <span className={`text-[10px] uppercase tracking-wider font-semibold flex items-center gap-1 ${color}`}><Icon className="w-3 h-3" /> {title}</span>
        <label className="inline-flex">
          <input type="file" accept="image/*" className="hidden" data-testid={`${testidBase}-add-input`} onChange={(e) => { const f = e.target.files?.[0]; if (f && onAdd) onAdd(f); e.target.value = ""; }} />
          <span className="inline-flex items-center h-6 px-1.5 rounded bg-ink text-white text-[9px] font-medium cursor-pointer hover:bg-ink/90"><Plus className="w-2.5 h-2.5 mr-0.5" /> Add</span>
        </label>
      </div>
      {items.length === 0 ? (
        <div className="text-[10px] text-ink/30 italic text-center py-2 border border-dashed border-border rounded">No items</div>
      ) : (
        <div className="space-y-1.5">
          {items.map((it, i) => (
            <div key={it.id || i} className="p-1 rounded border border-border" data-testid={`${testidBase}-item-${i}`}>
              <div className="flex items-center gap-1.5">
                {renderItem(it, i)}
                <button type="button" onClick={() => onRemove && onRemove(i)} data-testid={`${testidBase}-remove-${i}`} className="text-ink/40 hover:text-danger p-0.5">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
              <SliderRow label="Size (px)" value={it.height_px ?? sizeDefault} min={16} max={sizeMax} onChange={(v) => onPatch(i, { height_px: v })} testid={`${testidBase}-size-${i}`} />
            </div>
          ))}
        </div>
      )}
      <p className="text-[9px] text-ink/40 mt-1">{addLabel} — saved automatically.</p>
    </div>
  );
}

/* ============================================================
   Small building blocks
   ============================================================ */
function Group({ icon: Icon, label, expanded, onToggle, children }) {
  return (
    <div className="mb-3 rounded-lg border border-border bg-white overflow-hidden">
      <button onClick={onToggle} className="w-full flex items-center justify-between px-2.5 py-2 text-xs font-medium text-ink hover:bg-muted/40" data-testid={`studio-group-${label.toLowerCase().replace(/\s+/g, "-")}`}>
        <span className="flex items-center gap-2"><Icon className="w-3.5 h-3.5 text-ink/50" />{label}</span>
        {expanded ? <ChevronDown className="w-3.5 h-3.5 text-ink/40" /> : <ChevronRight className="w-3.5 h-3.5 text-ink/40" />}
      </button>
      {expanded && <div className="p-2 space-y-1.5 border-t border-border">{children}</div>}
    </div>
  );
}

function SectionCard({ title, children }) {
  return (
    <div className="rounded-lg border border-border bg-white p-2.5">
      <div className="text-[10px] uppercase tracking-wider text-ink/50 mb-1.5">{title}</div>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function ColorRow({ label, value, onChange, testid }) {
  return (
    <div className="flex items-center gap-2 px-1">
      <span className="text-[10px] text-ink/60 flex-1 truncate">{label}</span>
      <input type="color" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-8 h-7 rounded cursor-pointer border border-border" />
      <input value={value} onChange={(e) => onChange(e.target.value)} className="w-20 h-7 rounded border border-border px-1 text-[10px] font-mono" />
    </div>
  );
}

function SliderRow({ label, value, min = 0, max = 100, onChange, testid }) {
  const v = Number.isFinite(value) ? value : min;
  return (
    <div className="flex items-center gap-2 px-1">
      <span className="text-[10px] text-ink/60 flex-1 truncate min-w-0">{label}</span>
      <input type="range" min={min} max={max} value={v} onChange={(e) => onChange(parseInt(e.target.value, 10))} data-testid={testid} className="w-24 h-1 accent-efgh-blue cursor-pointer" />
      <span className="text-[10px] text-ink/60 tabular-nums w-8 text-right">{v}</span>
    </div>
  );
}

function SlotRow({ label, value, choices, onChange, testid }) {
  const arr = Array.isArray(value) ? value : (value && value !== "none" ? [value] : []);
  const [adding, setAdding] = useState("");
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);

  const add = () => {
    if (!adding || arr.includes(adding)) { setAdding(""); return; }
    onChange([...arr, adding]);
    setAdding("");
  };
  const removeAt = (i) => {
    const next = arr.filter((_, idx) => idx !== i);
    const cleaned = next.filter((c, idx) => !(c === "__break__" && (idx === 0 || idx === next.length - 1 || next[idx - 1] === "__break__")));
    onChange(cleaned.length === 0 ? "none" : cleaned);
  };
  const addRowBreak = () => {
    if (arr.length === 0 || arr[arr.length - 1] === "__break__") return;
    onChange([...arr, "__break__"]);
  };
  const moveItem = (from, to) => {
    if (from === to || from < 0 || to < 0 || from >= arr.length || to > arr.length) return;
    const next = [...arr];
    const [moved] = next.splice(from, 1);
    // If dropping past the original slot, `to` decreases by 1 after splice.
    const insertAt = to > from ? to - 1 : to;
    next.splice(insertAt, 0, moved);
    // Clean up any leading/trailing/dupe breaks the reorder may have produced.
    const cleaned = next.filter((c, idx) => !(c === "__break__" && (idx === 0 || idx === next.length - 1 || next[idx - 1] === "__break__")));
    onChange(cleaned);
  };

  const onDragStart = (i) => (e) => {
    setDragIdx(i);
    e.dataTransfer.effectAllowed = "move";
    // Firefox needs data to actually start dragging.
    try { e.dataTransfer.setData("text/plain", String(i)); } catch (_err) { /* older browsers */ }
  };
  const onDragOver = (i) => (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setDragOverIdx(i); };
  const onDrop = (i) => (e) => { e.preventDefault(); if (dragIdx !== null) moveItem(dragIdx, i); setDragIdx(null); setDragOverIdx(null); };
  const onDragEnd = () => { setDragIdx(null); setDragOverIdx(null); };

  return (
    <div className="border border-border rounded-md p-1.5" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-ink/50 mb-1">{label}</div>
      <div className="flex flex-wrap items-center gap-1 mb-1.5 min-h-[20px]" onDragOver={(e) => e.preventDefault()}>
        {arr.length === 0 && <span className="text-[10px] text-ink/30 italic">— empty</span>}
        {arr.map((k, i) => {
          const dropIndicator = dragOverIdx === i && dragIdx !== null && dragIdx !== i ? "ring-2 ring-efgh-orange/60" : "";
          if (k === "__break__") {
            return (
              <div key={`br-${i}`}
                draggable
                onDragStart={onDragStart(i)}
                onDragOver={onDragOver(i)}
                onDrop={onDrop(i)}
                onDragEnd={onDragEnd}
                className={`basis-full flex items-center gap-1 my-0.5 cursor-grab ${dropIndicator}`}
                data-testid={`${testid}-break-${i}`}
              >
                <GripVertical className="w-3 h-3 text-efgh-orange/40 shrink-0" />
                <div className="flex-1 h-px bg-efgh-orange/40" />
                <span className="text-[8px] uppercase tracking-widest text-efgh-orange/70 px-1">new row</span>
                <button type="button" onClick={() => removeAt(i)} className="text-efgh-orange/50 hover:text-danger text-[10px]" title="Remove row break">×</button>
                <div className="flex-1 h-px bg-efgh-orange/40" />
              </div>
            );
          }
          const c = choices.find((x) => x.k === k);
          return (
            <span
              key={`${k}-${i}`}
              draggable
              onDragStart={onDragStart(i)}
              onDragOver={onDragOver(i)}
              onDrop={onDrop(i)}
              onDragEnd={onDragEnd}
              data-testid={`${testid}-chip-${i}`}
              className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-efgh-blue/10 text-efgh-blue text-[10px] cursor-grab select-none ${dragIdx === i ? "opacity-40" : ""} ${dropIndicator}`}
            >
              <GripVertical className="w-2.5 h-2.5 text-efgh-blue/40" />
              {c?.n || k}
              <button type="button" onClick={() => removeAt(i)} className="text-efgh-blue/60 hover:text-danger">×</button>
            </span>
          );
        })}
      </div>
      <div className="flex items-center gap-1">
        <select value={adding} onChange={(e) => setAdding(e.target.value)} className="h-7 flex-1 min-w-0 rounded border border-border bg-white px-1 text-[10px]">
          <option value="">+ add field…</option>
          {choices.filter((c) => c.k !== "none" && !arr.includes(c.k)).map((c) => <option key={c.k} value={c.k}>{c.n}</option>)}
        </select>
        <button type="button" onClick={add} disabled={!adding} className="h-7 px-2 rounded bg-ink text-white text-[10px] hover:bg-ink/90 disabled:opacity-40">Add</button>
        <button type="button" onClick={addRowBreak} disabled={arr.length === 0 || arr[arr.length - 1] === "__break__"} data-testid={`${testid}-add-row-break`} title="Insert row break — anything after this appears on a new row" className="h-7 px-2 rounded border border-efgh-orange/40 text-efgh-orange text-[10px] hover:bg-efgh-orange/10 disabled:opacity-40">↵ Row</button>
      </div>
      {arr.length > 1 && (
        <div className="text-[9px] text-ink/30 mt-1 flex items-center gap-1"><GripVertical className="w-2.5 h-2.5" /> drag chips to reorder</div>
      )}
    </div>
  );
}

export default LetterheadStudio;

/* ============================================================
   CoversGroup — front + back PDF cover management inside the studio.
   Gated per-lab by super-admin flags on platform settings.
   ============================================================ */
function CoversGroup({ form, setForm, lab }) {
  const { platform, refresh } = useSiteSettings();
  useEffect(() => { if (refresh) refresh(); }, [refresh]);
  const frontEnabled = (platform?.cover_page_enabled_labs || []).includes(lab?.id);
  const backEnabled = (platform?.back_cover_enabled_labs || []).includes(lab?.id);
  return (
    <div className="space-y-3" data-testid="studio-covers-group">
      <CoverSlot
        title="Front cover (page 1)"
        endpoint={`/labs/${lab?.id}/cover-page`}
        currentUrl={form?.cover_page_url}
        currentName={form?.cover_page_filename}
        enabled={frontEnabled}
        onChange={(next) => setForm((prev) => ({ ...prev, cover_page_url: next.url ?? null, cover_page_path: next.path ?? null, cover_page_filename: next.filename ?? null }))}
        testidBase="front-cover"
      />
      <CoverSlot
        title="Back cover (last page)"
        endpoint={`/labs/${lab?.id}/back-cover`}
        currentUrl={form?.back_cover_url}
        currentName={form?.back_cover_filename}
        enabled={backEnabled}
        onChange={(next) => setForm((prev) => ({ ...prev, back_cover_url: next.url ?? null, back_cover_path: next.path ?? null, back_cover_filename: next.filename ?? null }))}
        testidBase="back-cover"
      />
    </div>
  );
}

function CoverSlot({ title, endpoint, currentUrl, currentName, enabled, onChange, testidBase }) {
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const fileRef = useRef(null);

  const onFile = async (file) => {
    if (!file) return;
    if (file.type !== "application/pdf") { toast.error("Only PDF files are supported"); return; }
    if (file.size > 1 * 1024 * 1024) { toast.error("PDF must be ≤ 1 MB"); return; }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const r = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange({ url: r.data.url, path: r.data.path, filename: r.data.filename });
      toast.success(`${title} uploaded`);
    } catch (e) { toast.error(apiError(e, "Upload failed")); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const onRemove = async () => {
    if (!window.confirm(`Remove the ${title.toLowerCase()}? Future report downloads will no longer include it.`)) return;
    setRemoving(true);
    try {
      await api.delete(endpoint);
      onChange({ url: null, path: null, filename: null });
      toast.success(`${title} removed`);
    } catch (e) { toast.error(apiError(e, "Remove failed")); }
    finally { setRemoving(false); }
  };

  if (!enabled) {
    return (
      <div className="rounded-md border border-dashed border-border p-2 bg-muted/20" data-testid={`${testidBase}-locked`}>
        <div className="flex items-center gap-1.5 text-[11px] font-medium text-ink/60">
          <FileImage className="w-3 h-3" /> {title}
          <span className="ml-auto text-[9px] px-1 py-0.5 rounded bg-muted text-ink/40">Locked</span>
        </div>
        <p className="text-[9px] text-ink/40 mt-1 leading-snug">Opt-in feature. Ask EFGH support to enable {title.toLowerCase()} for your lab.</p>
      </div>
    );
  }

  return (
    <div className="rounded-md border border-border p-2 bg-white" data-testid={`${testidBase}-card`}>
      <div className="flex items-center gap-1.5 text-[11px] font-medium text-ink mb-1.5">
        <FileImage className="w-3 h-3 text-efgh-blue" /> {title}
      </div>
      <label className="block border-2 border-dashed border-border rounded-md p-2 text-center bg-muted/30 hover:bg-muted/60 cursor-pointer">
        <input ref={fileRef} type="file" accept="application/pdf" className="hidden" data-testid={`${testidBase}-file-input`} onChange={(e) => onFile(e.target.files?.[0])} />
        <Upload className="w-3.5 h-3.5 mx-auto text-ink/50" />
        <div className="text-[10px] font-medium text-ink mt-1">
          {uploading ? "Uploading…" : currentUrl ? "Replace PDF" : "Upload PDF"}
        </div>
        <div className="text-[9px] text-ink/40">PDF · max 1 MB</div>
      </label>
      {currentUrl && (
        <div className="mt-1.5 flex items-center gap-1.5" data-testid={`${testidBase}-current`}>
          <div className="text-[10px] font-medium text-ink truncate flex-1" title={currentName || "cover.pdf"}>{currentName || "cover.pdf"}</div>
          <a href={currentUrl} target="_blank" rel="noreferrer" className="text-efgh-blue hover:text-efgh-blue/70" data-testid={`${testidBase}-preview`} title="Preview">
            <ExternalLink className="w-3 h-3" />
          </a>
          <button type="button" onClick={onRemove} disabled={removing} className="text-ink/40 hover:text-danger" data-testid={`${testidBase}-remove`} title={removing ? "Removing…" : "Remove"}>
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      )}
    </div>
  );
}

/* ============================================================
   PresetsModal — one-click letterhead styles.
   Fetches super-admin-managed templates from `/api/public/letterhead-templates`
   with the hardcoded `LETTERHEAD_PRESETS` as a graceful fallback.
   ============================================================ */
function PresetsModal({ onClose, onApply }) {
  const [items, setItems] = useState(LETTERHEAD_PRESETS);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/public/letterhead-templates");
        if (!cancelled && Array.isArray(r.data) && r.data.length > 0) setItems(r.data);
      } catch (_e) { /* fallback to hardcoded */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" onClick={onClose} data-testid="presets-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="border-b border-border px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Wand2 className="w-4 h-4 text-efgh-orange" />
            <h2 className="font-heading font-semibold text-ink">Preset letterhead templates</h2>
            {loading && <span className="text-[10px] text-ink/40">loading…</span>}
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-muted text-ink/60" data-testid="presets-close">×</button>
        </div>
        <div className="p-5 grid sm:grid-cols-2 gap-4 overflow-y-auto">
          {items.map((p) => (
            <button key={p.id} onClick={() => onApply(p)} data-testid={`preset-card-${p.id}`}
              className="text-left rounded-xl border border-border p-4 hover:border-efgh-orange hover:shadow-md transition-all group bg-white">
              <div className="flex items-start justify-between mb-2">
                <h3 className="font-heading font-semibold text-ink text-base group-hover:text-efgh-orange">{p.name}</h3>
                <div className="flex gap-1">
                  {(p.swatches || []).map((s, i) => (
                    <span key={i} className="w-4 h-4 rounded-full border border-border" style={{ background: s }} />
                  ))}
                </div>
              </div>
              <p className="text-xs text-ink/60 leading-snug">{p.tagline}</p>
              <div className="mt-3 pt-3 border-t border-border flex items-center justify-between">
                <span className="text-[10px] text-ink/40 uppercase tracking-wider">{p.is_system ? "System" : "Custom"} · 1-click apply</span>
                <span className="text-xs text-efgh-orange font-medium opacity-0 group-hover:opacity-100 transition-opacity">Apply →</span>
              </div>
            </button>
          ))}
        </div>
        <div className="border-t border-border px-5 py-3 flex items-center justify-between text-[11px] text-ink/50">
          <span>Templates only overwrite touched keys — your custom fields stay intact.</span>
          <button onClick={onClose} className="text-ink/60 hover:text-ink font-medium">Cancel</button>
        </div>
      </div>
    </div>
  );
}
